import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Package {
  id: string
  tracking_number: string
  package_type: string
  carrier: string
  sender: string
  recipient: string
  department: string
  received_date: string
  status: string
  is_outgoing: boolean
}

interface DeliveryLog {
  id: string
  item_type: string
  action: string
  performed_by: string
  timestamp: string
}

export default function MailroomPage() {
  const [packages, setPackages] = useState<Package[]>([])
  const [deliveryLogs, setDeliveryLogs] = useState<DeliveryLog[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [pkgRes, logsRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/mailroom/packages'),
      fetch(API_BASE + '/mailroom/delivery-logs'),
      fetch(API_BASE + '/mailroom/statistics')
    ])
    if (pkgRes.ok) setPackages((await pkgRes.json()).packages || [])
    if (logsRes.ok) setDeliveryLogs((await logsRes.json()).delivery_logs || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return 'bg-green-100 text-green-800'
      case 'ready_pickup': return 'bg-blue-100 text-blue-800'
      case 'processing': case 'received': return 'bg-yellow-100 text-yellow-800'
      case 'returned': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Mail & Package Handling</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Log Package</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Packages Today</div>
              <div className="text-2xl font-bold">{statistics.total_packages_today}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Incoming</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.incoming_packages}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pending Pickup</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.pending_pickup}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Delivered Today</div>
              <div className="text-2xl font-bold text-green-600">{statistics.delivered_today}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pending Mail</div>
              <div className="text-2xl font-bold">{statistics.pending_mail}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Packages</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Package</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipient</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {packages.slice(0, 12).map(pkg => (
                  <tr key={pkg.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {pkg.is_outgoing && <span className="text-xs bg-orange-100 text-orange-800 px-1 rounded">OUT</span>}
                        <div>
                          <div className="font-medium font-mono text-sm">{pkg.tracking_number}</div>
                          <div className="text-xs text-gray-500 capitalize">{pkg.package_type}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{pkg.recipient}</div>
                      <div className="text-xs text-gray-500">{pkg.department}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{pkg.carrier}</div>
                      <div className="text-xs text-gray-500">{pkg.received_date}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(pkg.status)}`}>{pkg.status.replace(/_/g, ' ')}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
            <div className="space-y-3">
              {deliveryLogs.slice(0, 12).map(log => (
                <div key={log.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm capitalize">{log.action}</span>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">{log.item_type}</span>
                  </div>
                  <div className="text-xs text-gray-500">{log.performed_by}</div>
                  <div className="text-xs text-gray-400 mt-1">{new Date(log.timestamp).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
